import { inject, Injector, Service } from '@angular/core';
import type {
  OnyxFilter,
  OnyxHttpErrorResponse,
  OnyxPaginated,
  OnyxPagination,
} from '@onyx/angular';
import { OnyxToastService } from '@onyx/angular';
import { omit } from 'lodash';
import type { Observable } from 'rxjs';
import {
  catchError,
  concatMap,
  defaultIfEmpty,
  from,
  map,
  of,
  Subject,
  switchMap,
  tap,
} from 'rxjs';
import type { RoutePointStatusFormDto } from '../../../../common/components/route/route-point/route-point-status-form/route-point-status-form.component';
import { VALIDATION_ERROR_CODES } from '../../../../common/constants/common/validation-error-codes';
import { FilterHelper } from '../../../../common/helpers/filter.helper';
import { RouteHelper } from '../../../../common/helpers/route.helper';
import { ValidationHelper } from '../../../../common/helpers/validation.helper';
import type { Activity } from '../../../../common/interfaces/common/activity';
import type { BatchFileUpload } from '../../../../common/interfaces/utilities/batch-file-upload';
import { ApiService } from '../../../../common/services/api.service';
import { TelematicsDataService } from '../../../../common/services/telematics-data.service';
import { TelematicsStore } from '../../../../common/stores/telematics.store';
import type { ImportProgress } from '../../../common/interfaces/import-progress';
import { FleetService } from '../../../fleet/common/services/fleet.service';
import type { CompanyVehicleParameter } from '../../../management-panel/dictionaries/common/interfaces/company-vehicle-parameter';
import type { OrderModalInvoiceFormDto } from '../../order-modal/order-modal-sections/order-modal-summary/order-modal-finished-section/order-modal-invoice-form/order-modal-invoice-form.component';
import type { OrderModalPaymentDto } from '../../order-modal/order-modal-sections/order-modal-summary/order-modal-finished-section/order-modal-payment-form-container/order-modal-payment-form-container.component';
import type { OrderModalOutsourcingFormDto } from '../../order-modal/order-modal-sections/order-modal-summary/order-modal-summary-main-section/order-modal-outsourcing-form/order-modal-outsourcing-form.component';
import type { OrderCancelFormDto } from '../components/order-cancel-modal/order-cancel-modal.component';
import type { OrderImportData } from '../constants/orders-import-schema';
import type { OrderDispatch } from '../enums/order-dispatch';
import { OrderPointStatus } from '../enums/order-point-status';
import type { OrderRejectReason } from '../enums/order-reject-reason';
import type { OrderSort } from '../enums/order-sort';
import { OrderStatus } from '../enums/order-status';
import type { OrderFormDto } from '../helpers/order-form.helper';
import type { OrderProposal } from '../interfaces/order';
import type { OrderImportDto } from '../interfaces/order-import';
import type { SimplifiedRoutePoint } from '../interfaces/simplified-route-point';
import { StorageService } from './../../../../common/services/storage.service';
import type { Order, OrderReference } from './../interfaces/order';

export interface OrdersFilters {
  filters?: OnyxFilter[];
  query?: string;
  sort?: OrderSort;
}

@Service()
export class OrdersService extends ApiService {
  private readonly fleetService = inject(FleetService);
  private readonly storageService = inject(StorageService);
  private readonly injector = inject(Injector);
  private readonly toastService = inject(OnyxToastService);

  private _reload$ = new Subject<void>();
  public get reload$() {
    return this._reload$.asObservable();
  }

  public listOrders(
    params: OnyxPagination & OrdersFilters,
  ): Observable<OnyxPaginated<Order>> {
    return this.get<OnyxPaginated<Order>>('/orders', {
      params: {
        page: params.page,
        limit: params.limit,
        ...(params.filters && FilterHelper.buildQueryParams(params.filters)),
        ...(params.query && { query: params.query }),
        ...(params.sort && { 'sort[]': params.sort }),
      },
    });
  }

  public getOrder(uuid: string): Observable<Order> {
    return this.get(`/orders/${uuid}`);
  }

  public addOrder(dto: OrderFormDto): Observable<OrderReference> {
    return this.post<OrderReference>('/orders', dto).pipe(
      tap(() => this.reloadRelated()),
    );
  }

  public importOrders(data: OrderImportData[]): Observable<ImportProgress> {
    const IMPORT_OMIT_KEYS: (keyof OrderImportDto)[] = [
      'sender',
      'senderBranch',
      'ourBranch',
      'loadingPoint',
      'good',
      'unloadingPoint',
    ];

    const dto = data.map(
      (item) =>
        ({
          ...omit(item, 'index', [...IMPORT_OMIT_KEYS]),
          parameters: {
            ...item.parameters,
            otherVehicleParameters:
              item.parameters.otherVehicleParameters?.map(
                (parameter: CompanyVehicleParameter) => parameter.uuid,
              ) ?? null,
            otherDriverPermissions:
              item.parameters.otherDriverPermissions?.map(
                (parameter: CompanyVehicleParameter) => parameter.uuid,
              ) ?? null,
          },
        }) as OrderFormDto,
    );

    return from(dto).pipe(
      concatMap((dto, index) =>
        this.addOrder(dto).pipe(
          map(() => ({ ok: true, index })),
          defaultIfEmpty({ ok: false, index }),
          catchError((response) => {
            ValidationHelper.handleUnexpectedResponse(response, this.injector);
            return of({ ok: false, index });
          }),
        ),
      ),
    );
  }

  public editOrder(
    uuid: string,
    dto: OrderFormDto,
  ): Observable<OrderReference> {
    return this.put<OrderReference>(`/orders/${uuid}`, dto).pipe(
      tap(() => this.reloadRelated()),
    );
  }

  public dispatch(uuid: string, dispatch: OrderDispatch): Observable<void> {
    return this.post<void>(`/orders/${uuid}/dispatch`, { dispatch }).pipe(
      tap(() => this.reloadRelated()),
    );
  }

  public moveToDraft(uuid: string): Observable<void> {
    return this.post<void>(`/orders/${uuid}/draft`, undefined).pipe(
      tap(() => this.reloadRelated()),
    );
  }

  public deleteOrder(uuid: string): Observable<void> {
    return this.delete<void>(`/orders/${uuid}`).pipe(
      tap(() => this.reloadRelated()),
    );
  }

  public acceptVehicle(uuid: string, vehicleUuid: string): Observable<void> {
    return this.post<void>(`/orders/${uuid}/engine-proposal/accept`, {
      vehicleUuid,
    }).pipe(tap(() => this.reloadRelated()));
  }

  public rejectVehicle(
    uuid: string,
    dto: { vehicleUuid: string } & (
      | { reason: OrderRejectReason; other: null }
      | { reason: OrderRejectReason.OTHER; other: string }
    ),
  ): Observable<void> {
    return this.post<void>(`/orders/${uuid}/engine-proposal/reject`, dto).pipe(
      tap(() => this.reloadRelated()),
    );
  }

  public addManualProposal(
    uuid: string,
    vehicleUuid: string,
    route: SimplifiedRoutePoint[],
  ): Observable<OrderProposal> {
    return this.post<OrderProposal>(`/orders/${uuid}/manual-proposal`, {
      vehicleUuid,
      route,
    }).pipe(tap(() => this.reloadRelated()));
  }

  public forceManualProposal(
    uuid: string,
    vehicleUuid: string,
  ): Observable<void> {
    return this.post<void>(`/orders/${uuid}/manual-proposal/force`, {
      vehicleUuid,
    }).pipe(
      tap(() => this.reloadRelated()),
      catchError((response: OnyxHttpErrorResponse) =>
        this.handleForceManualProposalError(response),
      ),
    );
  }

  public deleteManualProposal(uuid: string): Observable<void> {
    return this.delete<void>(`/orders/${uuid}/manual-proposal`).pipe(
      tap(() => this.reloadRelated()),
    );
  }

  public confirmManualProposal(
    uuid: string,
    vehicleUuid: string,
  ): Observable<void> {
    return this.post<void>(`/orders/${uuid}/manual-proposal/confirm`, {
      vehicleUuid,
    }).pipe(tap(() => this.reloadRelated()));
  }

  public addOutsourcing(
    uuid: string,
    dto: OrderModalOutsourcingFormDto,
  ): Observable<void> {
    return this.post<void>(`/orders/${uuid}/outsourcing`, dto).pipe(
      tap(() => this._reload$.next()),
    );
  }

  public editOutsourcing(
    uuid: string,
    dto: OrderModalOutsourcingFormDto,
  ): Observable<void> {
    return this.put<void>(`/orders/${uuid}/outsourcing`, dto).pipe(
      tap(() => this._reload$.next()),
    );
  }

  public deleteOutsourcing(uuid: string): Observable<void> {
    return this.delete<void>(`/orders/${uuid}/outsourcing`).pipe(
      tap(() => this._reload$.next()),
    );
  }

  public editPointStatus(
    orderUuid: string,
    pointUuid: string,
    fleetUuid: string | null,
    dto: RoutePointStatusFormDto,
  ): Observable<void> {
    return this.put<void>(
      `/orders/${orderUuid}/points/${pointUuid}/status`,
      dto,
    ).pipe(
      tap(() =>
        this.updateTelematicsOrderPointStatus(
          orderUuid,
          pointUuid,
          fleetUuid,
          dto,
        ),
      ),
      tap(() => this._reload$.next()),
      catchError((response) =>
        RouteHelper.handlePointStatusError(response, this.toastService),
      ),
    );
  }

  public completeDocuments(uuid: string): Observable<void> {
    return this.post<void>(
      `/orders/${uuid}/complete-documents`,
      undefined,
    ).pipe(tap(() => this._reload$.next()));
  }

  public addInvoice(
    uuid: string,
    dto: OrderModalInvoiceFormDto,
  ): Observable<void> {
    return this.uploadInvoiceFiles(dto).pipe(
      switchMap((dto) => this.post<void>(`/orders/${uuid}/invoice`, dto)),
      tap(() => this._reload$.next()),
    );
  }

  public addPayment(uuid: string, dto: OrderModalPaymentDto): Observable<void> {
    return this.post<void>(`/orders/${uuid}/payment`, dto).pipe(
      tap(() => this._reload$.next()),
    );
  }

  public editPayment(
    uuid: string,
    dto: OrderModalPaymentDto,
    paymentUuid: string,
  ): Observable<void> {
    return this.put<void>(`/orders/${uuid}/payment/${paymentUuid}`, dto).pipe(
      tap(() => this._reload$.next()),
    );
  }

  public deletePayment(uuid: string, paymentUuid: string): Observable<void> {
    return this.delete<void>(`/orders/${uuid}/payment/${paymentUuid}`).pipe(
      tap(() => this._reload$.next()),
    );
  }

  public getOrderActivity(uuid: string): Observable<Activity> {
    return this.get(`/orders/${uuid}/activity`);
  }

  public archive(uuid: string): Observable<void> {
    return this.post<void>(`/orders/${uuid}/archive`, undefined).pipe(
      tap(() => this.reloadRelated()),
    );
  }

  public cancel(uuid: string, dto: OrderCancelFormDto): Observable<void> {
    return this.post<void>(`/orders/${uuid}/cancel`, dto).pipe(
      tap(() => this.reloadRelated()),
    );
  }

  private uploadInvoiceFiles(
    dto: OrderModalInvoiceFormDto,
  ): Observable<OrderModalInvoiceFormDto> {
    return of(dto).pipe(
      map((dto): BatchFileUpload[] => [
        {
          path: 'scan',
          files: dto.scan,
        },
      ]),
      switchMap((data) => this.storageService.uploadBatch(data)),
      map((data) => this.storageService.mergeBatch(dto, data)),
    );
  }

  private updateTelematicsOrderPointStatus(
    orderUuid: string,
    pointUuid: string,
    fleetUuid: string | null,
    dto: RoutePointStatusFormDto,
  ): void {
    if (!fleetUuid) return;

    const telematicsService = this.injector.get(TelematicsDataService);
    const { arrivedAt, startedAt, completedAt, status } = dto;

    telematicsService.updateOrderPointStatus({
      fleetUuid,
      orderPointUuid: pointUuid,
      status,
      timestamps: {
        arrivedAt,
        startedAt,
        completedAt,
        canceledAt: null,
      },
    });

    const telematicsStore = this.injector.get(TelematicsStore);
    const telematicsItem = telematicsStore.getItem(fleetUuid);
    if (!telematicsItem) return;

    const routePoints = telematicsItem.routePoints;
    const orderPoint = routePoints.find(
      (point) => RouteHelper.getUuid(point) === pointUuid,
    );
    const order = orderPoint?.order;
    if (!order || order.uuid !== orderUuid) return;

    const isCompleted =
      orderPoint.point?.index === order.pointsCount - 1 &&
      status === OrderPointStatus.COMPLETED;
    const isStarted = routePoints.some(
      (point) =>
        point.order?.uuid === orderUuid &&
        (RouteHelper.getUuid(point) === pointUuid
          ? status
          : RouteHelper.getStatus(point)) !== OrderPointStatus.TO_DO,
    );

    const updatedStatus = isCompleted
      ? OrderStatus.COMPLETING_DOCUMENTS
      : isStarted
        ? OrderStatus.IN_PROGRESS
        : null;
    if (!updatedStatus || updatedStatus === order.status.value) return;

    telematicsService.updateOrderStatus({
      fleetUuid,
      orderUuid,
      status: {
        ...order.status,
        value: updatedStatus,
      },
    });
  }

  private handleForceManualProposalError(
    response: OnyxHttpErrorResponse,
  ): Observable<never> {
    if (!VALIDATION_ERROR_CODES.includes(response.status)) throw response;

    const error = JSON.stringify(response.error ?? '');

    if (error.includes('Manual proposal result must be empty')) {
      this.toastService.showError({
        message: 'orders.orderModal.toasts.manualProposalCalculated',
        buttons: [
          {
            text: 'labels.refresh',
            callback: () => this.reloadRelated(),
            color: 'black',
            variant: 'ghost',
            size: 24,
          },
        ],
      });
      response.handled = true;
    }

    throw response;
  }

  private reloadRelated(): void {
    this._reload$.next();
    this.fleetService['_reload$'].next();
  }
}
